import { Visibility } from "@mui/icons-material";
import {
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { ChangeEvent, FormEvent, useContext, useState } from "react";
import { Global_CTX } from "../../contexts/global-ctx";
import {
    Absentee_Row,
    Attendance_Service,
    Get_Absenties_Filter,
} from "../../services/attendance/attendance-service";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { RUDTable } from "../Table/RUDTable";
import UserContext, { User_Type } from "../UserContext";
import Absenties_By_Date_List from "./absenties-by-date-list";

const initial_filter: Get_Absenties_Filter = {
    startDate: "",
    endDate: "",
    apiKey: "",
};

export default function Absenties_Page() {
    const [filter, set_filter] = useState(initial_filter);
    const [open, set_open] = useState(false);
    const [absenties_list_by_date, set_absenties_list_by_date] = useState<{
        list: Absentee_Row[];
        date: string;
    } | null>(null);
    const { user } = useContext(UserContext);
    const { refetch_counter, set_refetch_counter } = useContext(Global_CTX);

    function handle_change(event: ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;

        set_filter((f) => ({
            ...f,
            [name]: value,
        }));
    }

    function handle_apply_filter(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        set_refetch_counter((n) => n + 1);
    }

    return (
        <Box>
            <Grid
                container
                p={2}
                spacing={1}
                component="form"
                onSubmit={handle_apply_filter}
            >
                <Grid item xs={12} mb={1}>
                    <Typography
                        color={(theme) => theme.palette.primary.main}
                        variant="h5"
                    >
                        Absenties Reports
                    </Typography>
                </Grid>

                {user?.user_type === User_Type.Superadmin && (
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            label="Organization"
                            labelKey="name"
                            api="/organization"
                            setOutput={(org) => {
                                set_filter((f) => ({
                                    ...f,
                                    org_code: org?.code || "",
                                }));
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                        label="Branch"
                        labelKey="name"
                        api="/organization/branch"
                        setOutput={(val) => {
                            set_filter((f) => ({
                                ...f,
                                branch_id: val?.id || "",
                            }));
                        }}
                        apiParams={{
                            organization:
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : "",
                        }}
                        request_headers={{
                            "x-org":
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : "",
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        label="Start Date"
                        InputProps={{
                            type: "date",
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 200,
                            "& input::-webkit-calendar-picker-indicator": {
                                filter: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "invert(100%)"
                                        : undefined, // Adjust for desired color (e.g., green)
                            },
                        }}
                        value={filter.startDate}
                        onChange={handle_change}
                        name="startDate"
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <TextField
                        label="End Date"
                        InputProps={{
                            type: "date",
                            // endAdornment: <CalendarMonth fontSize="small" />,
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 200,
                            "& input::-webkit-calendar-picker-indicator": {
                                filter: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "invert(100%)"
                                        : undefined, // Adjust for desired color (e.g., green)
                            },
                        }}
                        value={filter.endDate}
                        onChange={handle_change}
                        name="endDate"
                        fullWidth
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                    <Button
                        type="submit"
                        sx={{ height: "100%" }}
                        variant="contained"
                        disabled={!filter.startDate || !filter.endDate}
                    >
                        Apply Filter
                    </Button>
                </Grid>
            </Grid>

            <RUDTable
                storageKey="absenties-report"
                updatingAgents={[refetch_counter]}
                getter={(page, limit) =>
                    Attendance_Service.get_absenties(
                        { ...filter, page, limit },
                        user?.user_type === User_Type.Admin
                            ? user.organization.code
                            : "",

                        user?.user_type === User_Type.Admin
                            ? user.organization.apiKey[0].key
                            : ""
                    )
                }
                rowsPreprocessor={([date, absenties]) => {
                    return {
                        date: new Date(date).toDateString(),
                        absenties_count: absenties.length,
                        absenties,
                    };
                }}
                ops={{
                    read: "",
                    // edit: undefined,
                    // delete: undefined,
                }}
                readables={{
                    date: "Date",
                    absenties_count: "Absenties",
                    absenties_list: "Details",
                }}
                customCols={[
                    {
                        header: "absenties_list",
                        content: (row) => (
                            <IconButton
                                size="small"
                                onClick={() => {
                                    set_open(true);
                                    set_absenties_list_by_date({
                                        date: row.date,
                                        list: row.absenties,
                                    });
                                }}
                            >
                                <Visibility fontSize="small" />
                            </IconButton>
                        ),
                    },
                ]}
            />

            <Dialog
                open={open}
                maxWidth="md"
                onClose={() => {
                    set_open(false);
                    setTimeout(() => {
                        set_absenties_list_by_date(null);
                    }, 500);
                }}
                fullWidth
            >
                <DialogTitle>
                    Absenties List for{" "}
                    {absenties_list_by_date ? absenties_list_by_date.date : ""}
                </DialogTitle>

                <DialogContent>
                    {absenties_list_by_date && (
                        <Absenties_By_Date_List
                            list={absenties_list_by_date.list}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
